import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';

const computeDays = (expiryDate) => {
  const s = String(expiryDate || '');
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const exp = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(s);
  if (Number.isNaN(exp.getTime())) return 9999;
  return Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

// Always derive the state from the expiry date. The stored daysRemaining /
// status are computed once when the document is saved and never refreshed,
// so a document expiring in two weeks could still read "OK" here. Same
// thresholds as the admin Alerts page (critical < 7 days, attention < 30).
const getState = (doc) => {
  const days = computeDays(doc.expiryDate);
  if (days < 7) return 'critical';
  if (days < 30) return 'attention';
  return 'ok';
};

const STATE_COLOR = { critical: '#dc2626', attention: '#f59e0b', ok: '#10b981' };
const STATE_LABEL = { critical: 'CRITICAL', attention: 'Expire', ok: 'OK' };

const parseFlexDate = (value) => {
  if (!value) return null;
  const s = String(value);
  const m1 = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m1) return new Date(`${m1[3]}-${m1[2]}-${m1[1]}T00:00:00`);
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return new Date(`${m2[1]}-${m2[2]}-${m2[3]}T00:00:00`);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (v) => parseFlexDate(v)?.toLocaleDateString() || '—';

const formatExpiry = (v) => {
  const s = String(v || '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) return s;
  return new Date(s).toLocaleDateString('en-GB');
};

const DriverAlerts = () => {
  const { user } = useAppContext();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // A driver's alerts are the documents of the vehicle(s) currently assigned
    // to them (same rule as the Vehicles tab) — not the driver name copied onto
    // each document when it was saved, which is empty or stale whenever the
    // driver was assigned after the document was added.
    Promise.all([apiEndpoints.documents.list(), apiEndpoints.vehicles.list()])
      .then(([docsRes, vehiclesRes]) => {
        const myId = user?.driverId || null;
        const myName = (user?.name || '').trim().toLowerCase();
        const normPlate = (p) => String(p || '').replace(/\s+/g, '').toUpperCase();
        const myVehicles = (vehiclesRes.data || []).filter((v) =>
          myId && (v.driverId === myId || v.subAssignment?.driverId === myId)
        );
        const myVehicleIds = new Set(myVehicles.map((v) => v.id));
        const myPlates = new Set(myVehicles.map((v) => normPlate(v.plate)).filter(Boolean));
        const mine = (docsRes.data || []).filter((d) => {
          if (String(d.vehicleId || '').startsWith('supplier-')) return false;
          if (myVehicleIds.has(d.vehicleId)) return true;
          const plate = normPlate(d.plate);
          if (plate && myPlates.has(plate)) return true;
          // Legacy fallback: documents that only carry the driver's name.
          return !!(myName && d.driver && d.driver.trim().toLowerCase() === myName);
        });
        setDocs(mine);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return <div className="page-loading">Loading alerts…</div>;

  const alertDocs    = docs.filter(d => getState(d) !== 'ok');
  const criticalDocs = docs.filter(d => getState(d) === 'critical');
  const attentionDocs = docs.filter(d => getState(d) === 'attention');
  const okDocs       = docs.filter(d => getState(d) === 'ok');

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selected) {
    const state = getState(selected);
    const statusColor = STATE_COLOR[state];
    const days = computeDays(selected.expiryDate);
    const daysText = days < 0 ? `${Math.abs(days)} days ago` : `${days} days left`;

    return (
      <div>
        <button className="back-btn" onClick={() => setSelected(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to alerts
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{selected.plate || selected.vehicle || 'UNKNOWN'}</span></div>
          <div className="vprofile-model">{selected.documentType || 'Alert'}</div>
          <div className="vprofile-sub">{selected.issuer || 'Unknown issuer'} • expires {fmtDate(selected.expiryDate)}</div>
        </div>

        <div className="detail-grid">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Alert Details</div>
              <span style={{ color: statusColor, fontWeight: 700 }}>{STATE_LABEL[state]}</span>
            </div>
            <div className="card-body">
              {[
                ['Vehicle', selected.vehicle || selected.plate || '—'],
                ['Document type', selected.documentType || '—'],
                ['Issuer', selected.issuer || '—'],
                ['Issued on', fmtDate(selected.issueDate)],
                ['Expires on', fmtDate(selected.expiryDate)],
                ['Driver', selected.driver || user?.name || '—'],
                ['Company', selected.company || '—'],
              ].map(([lbl, val]) => (
                <div key={lbl} className="info-row">
                  <div className="info-lbl">{lbl}</div>
                  <div className="info-val">{val}</div>
                </div>
              ))}
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <div className="info-lbl">Time remaining</div>
                <div className="info-val" style={{ color: statusColor, fontWeight: 700 }}>{daysText}</div>
              </div>
            </div>
          </div>

          <div className="card quick-actions-card">
            <div className="card-header"><div className="card-title">Status</div></div>
            <div className="card-body" style={{ display: 'grid', gap: '16px', textAlign: 'center', padding: '24px' }}>
              <div>
                <div style={{ fontSize: '32px', fontWeight: 800, color: statusColor }}>{STATE_LABEL[state]}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>document status</div>
              </div>
              <div style={{ background: days < 0 ? '#fef2f2' : days < 14 ? '#fffbeb' : '#ecfdf5', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: statusColor }}>{daysText}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>until expiry</div>
              </div>
              {selected.company && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>Company</div>
                  <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{selected.company}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">My Alerts</div>
          <div className="ph-sub">Documents requiring attention</div>
        </div>
      </div>

      <div className="alert-status-grid">
        <div className="alert-status-card alert-status-critical">
          <div className="alert-status-label">CRITICAL</div>
          <div className="alert-status-count">{criticalDocs.length}</div>
          <div className="alert-status-text">Immediate action</div>
        </div>
        <div className="alert-status-card alert-status-attention">
          <div className="alert-status-label">ATTENTION</div>
          <div className="alert-status-count">{attentionDocs.length}</div>
          <div className="alert-status-text">Plan renewal</div>
        </div>
        <div className="alert-status-card alert-status-ok">
          <div className="alert-status-label">OK</div>
          <div className="alert-status-count">{okDocs.length}</div>
          <div className="alert-status-text">All documents OK</div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="alerts-list">
            {alertDocs.length === 0 ? (
              <div className="empty-row" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
                All your documents are up to date.
              </div>
            ) : (
              alertDocs.map((doc) => {
                const state = getState(doc);
                const days = computeDays(doc.expiryDate);
                const daysText = days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`;
                return (
                  <div key={doc._id} className={`alert-item alert-item-${state}`} style={{ cursor: 'pointer' }} onClick={() => setSelected(doc)}>
                    <div className="alert-item-left">
                      <div className={`alert-item-indicator alert-indicator-${state}`} />
                      <div className="alert-item-plate">{doc.plate || 'N/A'}</div>
                    </div>
                    <div className="alert-item-content">
                      <div className="alert-item-title">
                        <span className="alert-item-driver">{doc.driver || user?.name}</span>
                        <span className="alert-item-company">—</span>
                        <span className="alert-item-company">{doc.company || '—'}</span>
                      </div>
                      <div className="alert-item-meta">
                        <span className="alert-doc-type">{doc.documentType || 'DOC'}</span>
                        <span>— issued by {doc.issuer || 'Issuer'}</span>
                        <span>— expires {formatExpiry(doc.expiryDate)}</span>
                      </div>
                    </div>
                    <div className="alert-item-days">
                      <div className={`alert-item-days-value alert-days-${state}`}>{daysText}</div>
                    </div>
                    <div className={`alert-item-badge alert-badge-${state}`}>{STATE_LABEL[state]}</div>
                    <div className="alert-item-actions">
                      <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); setSelected(doc); }}>View</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverAlerts;
